export * from "./gpt-admin";
export * from "./_internal/types.gen";
export { DEFAULT_API_VERSION, SDK_VERSION } from "./base-client";
export type { FlatResource } from "./json-api";
export type {
  VoiceAPI,
  AdminModelsAPI,
  AdminApplicationsAPI,
  AdminEmailAPI,
  AdminBillingAPI,
  AdminSchedulingAPI,
  AdminReviewsAPI,
  InvoicesAPI,
  RecipesAPI,
  AdminFormsAPI,
  AdminMeetingsAPI,
} from "./namespaces";

// Meetings (PR B10 + PR-A PHI encryption-at-rest)
export { createMeetingsNamespace } from "./namespaces/meetings";
export type {
  CreateMeetingAttributes,
  UpdateMeetingAttributes,
  MeetingAttendee,
} from "./namespaces/meetings";

// Crawler namespace + admin-only attribute types
// (BrowserSession / CrawlerJob / CrawlerResult / CrawlerSchedule /
// CrawlerSiteConfig / NewsMonitor / NewsSummary are exported via the
// top-of-file `export * from "./_internal/types.gen"`.)
export { createCrawlerNamespace } from "./namespaces/crawler";
export type {
  AdminCreateCrawlerJobAttributes,
  AdminCreateCrawlerScheduleAttributes,
  AdminUpdateCrawlerScheduleAttributes,
  AdminCreateSiteConfigAttributes,
  AdminUpdateSiteConfigAttributes,
  AdminCreateBrowserSessionAttributes,
  AdminRefreshBrowserSessionAttributes,
  CreateNewsMonitorAttributes,
  UpdateNewsMonitorAttributes,
  AdminCrawlerAPI,
} from "./namespaces/crawler";
export type {
  AdminApproveReviewAttributes,
  AdminAssignReviewAttributes,
  AdminCorrectReviewAttributes,
  AdminCreateReviewQueueAttributes,
  AdminCreateReviewQueueMembershipAttributes,
  AdminEscalateReviewAttributes,
  AdminRejectReviewAttributes,
  AdminReviewPriority,
  AdminReviewQueueMembershipRole,
  AdminUpdateReviewQueueAttributes,
} from "./namespaces";

// Forms (Slice 5) namespace helper types
export type {
  FormGenerationJob,
  CreateFormGenerationJobInput,
  ListFormGenerationsArgs,
  // Slice 5.5 — field-level data classification
  ClassifyFieldInput,
  ProtectionClass,
  // B7 — Form deployment governance
  CreateFormDeploymentAttributes,
  UpdateFormDeploymentAttributes,
  CreateWorkspaceFormConfigAttributes,
  UpdateWorkspaceFormConfigAttributes,
  SealPolicyShape,
} from "./namespaces/forms";

// Application namespace helper types
export type {
  CreateApplicationAttributes,
  UpdateApplicationAttributes,
  CurrentApplicationResult,
  OAuthConfig,
} from "./namespaces/applications";

// API key namespace helper types
export type {
  CreateApiKeyAttributes,
  ApiKeyUsageStat,
  UpdateApiKeyAttributes,
} from "./namespaces/apiKeys";

// Invitation namespace helper types
export type { InviteAttributes } from "./namespaces/invitations";

// User namespace helper types
export type {
  AdminUpdateUserAttributes,
  UpdateUserAttributes,
} from "./namespaces/users";

// User profile namespace helper types
export type {
  CreateUserProfileAttributes,
  UpdateUserProfileAttributes,
  LegalDocumentType,
} from "./namespaces/userProfiles";

// Tenant namespace helper types
export type {
  ConvertToOrgAttributes,
  CreateIsvTenantAttributes,
  CreateOrgTenantAttributes,
  CreatePersonalTenantAttributes,
  CreateTenantAttributes,
  UpdateAutoTopUpAttributes,
  UpdateBrandingAttributes,
  UpdateTenantAttributes,
} from "./namespaces/tenants";

// Claimed domain namespace helper types
export type {
  CreateClaimedDomainAttributes,
  UpdateClaimedDomainAttributes,
} from "./namespaces/claimed-domains";

// System message namespace helper types
export type {
  SystemMessageType,
  CreateSystemMessageAttributes,
  UpdateSystemMessageAttributes,
} from "./namespaces/system-messages";

// Model catalog types
export type {
  ModelTier,
  ModelInfo,
  ListModelsOptions,
} from "./namespaces/models";

// Billing namespace helper types
export type {
  CreatePlanAttributes,
  UpdatePlanAttributes,
  CreatePricingStrategyAttributes,
  CreateTenantPricingOverrideAttributes,
  CreateWholesaleAgreementAttributes,
  CreateSettlementAttributes,
  CreateFeatureDefinitionAttributes,
  CreatePlanFeatureAllocationAttributes,
  CreateCompositeOperationAttributes,
  EstimateCapacityResponse,
  SignupGrantStatus,
  CapabilityRevocationPreview,
  CanEnableResult,
  PlanBundleSpec,
  PlanBundlePublishResult,
  PlanBundleMix,
  PlanBundlePublishOptions,
  PlanBundleTemplate,
  CreateIsvPricingOverrideAttributes,
  UpdateIsvPricingOverrideAttributes,
} from "./namespaces/billing";

// Execution namespace helper types
export type { Execution, FileInput } from "./namespaces/executions";

// Agent namespace helper types
export type {
  CreateAgentVersionAttributes,
  CreateTrainingExampleAttributes,
  UpdateTrainingExampleAttributes,
  CreateFieldTemplateAttributes,
  TeachAttributes,
  RestoreVersionAttributes,
  AgentExportBundle,
  BulkDeleteResult,
} from "./namespaces/agents";

// Pipeline namespace helper types
export type {
  CreatePipelineAttributes,
  UpdatePipelineAttributes,
  TriggerPipelineAttributes,
} from "./namespaces/pipelines";

// PipelineExecution namespace types
export type {
  PipelineExecution,
  PipelineExecutionStatus,
  UpdateCheckpointAttributes,
  AiRefineAttributes,
} from "./namespaces/pipeline-executions";

// PipelineNode namespace types
export type {
  PipelineNode,
  PipelineNodeType,
  PipelineNodePhase,
  CreatePipelineNodeAttributes,
  UpdatePipelineNodeAttributes,
} from "./namespaces/pipeline-nodes";

// PipelineNodeExecution namespace types
export type {
  PipelineNodeExecution,
  PipelineNodeExecutionStatus,
} from "./namespaces/pipeline-node-executions";

export * from "./errors";
export type {
  StreamOptions,
  StreamMessageChunk,
  StreamTokenEvent,
  StreamToolCallEvent,
  StreamToolResultEvent,
  StreamIterationCompleteEvent,
  StreamApprovalRequiredEvent,
  StreamDoneEvent,
  StreamDoneMetadata,
  StreamErrorEvent,
} from "./streaming";
export type {
  ExecutionEvent,
  TokenDeltaEvent,
  ToolCallEvent,
  ToolResultEvent,
  IterationCompleteEvent,
  ApprovalRequiredEvent,
  DoneEvent,
  ErrorEvent,
} from "./execution-events";

// Thread namespace helper types
export type { UpdateThreadAttributes } from "./namespaces/threads";

// Channels namespace types
export { createChannelsNamespace } from "./namespaces/channels";
export type {
  ChannelTokenRequest,
  ChannelTokenResponse,
} from "./namespaces/channels";

// Clinical namespace types
export type {
  ClinicalAnalyticsOverview,
  ClinicalPipelineAnalytics,
  ClinicalClientGoal,
  ClinicalClientResourceAssignment,
  ClinicalClientSupplement,
  ClinicalDelivery,
  ClinicalGoalTemplate,
  ClinicalGoalTemplateCategory,
  ClinicalHealthMetric,
  ClinicalInsuranceAuthorization,
  ClinicalMealPlan,
  ClinicalNote,
  ClinicalNoteVersion,
  ClinicalPatient,
  ClinicalPatientInsurance,
  ClinicalPracticeResource,
  ClinicalPracticeTool,
  ClinicalPracticeToolCategory,
  ClinicalSession,
  ClinicalSupplementRecCache,
  CreateClinicalPatientAttributes,
  UpdateClinicalPatientAttributes,
  CreateGoalTemplateAttributes,
  UpdateGoalTemplateAttributes,
  CreateFromExistingAttributes,
  CreateInsuranceAuthorizationAttributes,
  UpdateInsuranceAuthorizationAttributes,
  CreatePatientInsuranceAttributes,
  UpdatePatientInsuranceAttributes,
  CreatePracticeToolAttributes,
  UpdatePracticeToolAttributes,
  CreatePracticeResourceAttributes,
  UpdatePracticeResourceAttributes,
  CreateCatalogGoalTemplateAttributes,
  UpdateCatalogGoalTemplateAttributes,
  CreateCatalogPracticeToolAttributes,
  UpdateCatalogPracticeToolAttributes,
  CreateCatalogPracticeResourceAttributes,
  UpdateCatalogPracticeResourceAttributes,
  ClinicalDocument,
  CreateDocumentAttributes,
  UpdateDocumentAttributes,
  DocumentType,
  ExtractionStatus,
} from "./namespaces/clinical";

// Audit namespace
export { createAuditNamespace } from "./namespaces/audit";

// Audit namespace types.
// `AuditLog` is intentionally re-exported as `AuditLogEntry` to avoid a
// collision with the generated `AuditLog` symbol from
// `_internal/types.gen.ts` (Ash JSON:API schema name). The hand-maintained
// `AuditLog` interface in `namespaces/audit.ts` is the public contract; the
// generated type is internal infrastructure surfaced via `export *` and
// is not the canonical shape ISVs should depend on. Renaming the public
// interface to drop the alias would be a breaking SDK change and require a
// major version bump — see F-C5 in the audit review (2026-05-15).
export type {
  AuditLog as AuditLogEntry,
  AuditListParams,
  ActivityFeedParams as AuditActivityFeedParams,
  CountByActionParams,
  CountByActionResult,
  ExportParams as AuditExportParams,
  ExportResult as AuditExportResult,
} from "./namespaces/audit";

// Authorization namespace types
export type {
  Role,
  RoleListResult,
  RoleResult,
  RoleListParams,
} from "./namespaces/roles";
export type {
  AccessGrant,
  AccessGrantListResult,
  AccessGrantResult,
  AccessGrantListParams,
} from "./namespaces/access-grants";
export type {
  Permission,
  PermissionMeta,
  PermissionPreset,
  PermissionListResult,
  PermissionResult,
  PermissionMetaResult,
  PermissionPresetListResult,
  PermissionPresetResult,
  PermissionListParams,
  PermissionPresetListParams,
} from "./namespaces/permissions";
export type {
  OwnershipTransfer,
  OwnershipTransferListResult,
  OwnershipTransferListParams,
} from "./namespaces/ownership-transfers";
// Compliance attribute types (F-27)
// Previously only `CreateCompliancePolicyAttributes` + update were exported.
// Admin namespace exposes additional create/update interfaces that ISVs
// writing typed wrappers couldn't reach without subpath imports.
export type {
  CreateCompliancePolicyAttributes,
  UpdateCompliancePolicyAttributes,
  CreateBreachIncidentAttributes,
  UpdateBreachIncidentStatusAttributes,
  CreateDataSubjectRequestAttributes,
  UpdateDataSubjectRequestStatusAttributes,
  CreateRetentionPolicyAttributes,
  UpdateRetentionPolicyAttributes,
  CreateRiskAssessmentAttributes,
  UpdateRiskAssessmentStatusAttributes,
  CreateLegalDocumentAttributes,
  UpdateLegalDocumentAttributes,
} from "./namespaces/compliance";

// Social namespace types
export type {
  SocialAccount,
  SocialPost,
  PostMetric,
  TrendingSnapshot,
  TrendingSnapshotItem,
  TrendingWatch,
} from "./_internal/types.gen";
export type { AdminSocialAPI } from "./namespaces";

// Campaigns namespace helper types
export type {
  CreateCampaignAttributes,
  CreateTemplateAttributes,
  UpdateTemplateAttributes,
  UpdateGeneratedEmailAttributes,
  CreateSequenceAttributes,
} from "./namespaces/campaigns";

// Invoices namespace helper types
export type {
  CreateInvoiceAttributes,
  UpdateInvoiceAttributes,
  CreateInvoiceLineAttributes,
  UpdateInvoiceLineAttributes,
  CreatePaymentAttributes,
  CreateTemplateAttributes as CreateInvoiceTemplateAttributes,
  UpdateTemplateAttributes as UpdateInvoiceTemplateAttributes,
  CreateRuleAttributes,
  UpdateRuleAttributes,
  CreateRecurringScheduleAttributes,
  UpdateRecurringScheduleAttributes,
  CreateContactAttributes as CreateInvoiceContactAttributes,
  UpdateContactAttributes as UpdateInvoiceContactAttributes,
} from "./namespaces/invoices";

// Import hub types
export { createImportsNamespace } from "./namespaces/imports";
export type { Import, ImportRow, ImportAdapter } from "./namespaces/imports";
export type { ListResponse } from "./request-builder";

// Connectors namespace helper types
export type {
  CreateConnectorInstanceAttributes,
  UpdateConnectorInstanceAttributes,
  ConnectorTool,
  CreateOAuthAppConfigAttributes,
  UpdateOAuthAppConfigAttributes,
  CreateConnectorToolAttributes,
  UpdateConnectorToolAttributes,
} from "./namespaces/connectors";

export type {
  ListSystemAgentsParams,
  SystemAgentSummary,
} from "./namespaces/agents";

// AI / LLM namespace helper types
export type {
  ProviderConfig,
  LlmCostBreakdown,
  LlmAnalyticsSummary,
  LlmAnalyticsUsage,
  LlmWorkspaceMetrics,
  LlmPlatformSummary,
  RecordLlmAnalyticsAttributes,
} from "./namespaces/ai";

// Re-export GptAdmin as default export for ESM compatibility
// This allows: import GptAdmin from '@gpt-platform/admin'
import { GptAdmin } from "./gpt-admin";
export default GptAdmin;
